import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, GitBranch, Code, Check, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { createPageUrl } from "@/utils";
import { SourceFieldMappingView, ReferenceList } from "@/entities/all";

const STATUS_CONFIG = {
  DRAFT:          { bg: 'rgba(127,199,249,0.20)', color: 'rgba(55,122,214,0.70)',  label: 'Draft' },
  APPROVED:       { bg: 'rgba(77,179,0,0.15)',    color: 'rgba(77,179,0,0.90)',    label: 'Approved' },
  PENDING_REVIEW: { bg: 'rgba(233,171,48,0.20)',  color: 'rgba(233,171,48,0.90)', label: 'Pending Review' },
  DEPRECATED:     { bg: 'rgba(222,111,107,0.20)', color: 'rgba(222,111,107,0.90)',label: 'Deprecated' },
};

function getDataTypePill(type) {
  const t = (type || '').toLowerCase();
  if (t === 'string' || t === 'large_string')
    return { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)', label: type };
  if (t.startsWith('int') || t.startsWith('uint'))
    return { bg: 'rgba(167,139,250,0.20)', color: 'rgba(109,40,217,0.85)', label: type };
  if (t.startsWith('float') || t === 'decimal128')
    return { bg: 'rgba(99,102,241,0.15)', color: 'rgba(79,70,229,0.90)', label: type };
  if (t === 'bool')
    return { bg: 'rgba(77,179,0,0.15)', color: 'rgba(77,179,0,0.90)', label: 'Boolean' };
  if (t.includes('date') || t.includes('time') || t === 'timestamp' || t === 'duration')
    return { bg: 'rgba(251,146,60,0.20)', color: 'rgba(234,88,12,0.85)', label: type };
  if (t === 'uuid')
    return { bg: 'rgba(6,182,212,0.15)', color: 'rgba(6,182,212,0.90)', label: 'UUID' };
  if (t === 'binary' || t === 'large_binary')
    return { bg: 'rgba(148,163,184,0.20)', color: 'rgba(100,116,139,0.90)', label: type };
  return { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)', label: type || '—' };
}

function Pill({ bg, color, label }) {
  return (
    <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2" style={{ backgroundColor: bg }}>
      <span className="size-[10px] rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="text-xs font-medium whitespace-nowrap leading-none" style={{ color }}>{label}</span>
    </span>
  );
}

function HoverPopupCell({ children, elementId, loadContent, navigate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const closeTimer = useRef(null);

  const handleOpen = async () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
    if (items === null && !loading) {
      setLoading(true);
      try {
        const result = await loadContent(elementId);
        setItems(result);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => { closeTimer.current = setTimeout(() => setOpen(false), 150); };
  const handleContentEnter = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <div onMouseEnter={handleOpen} onMouseLeave={handleClose} onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start" sideOffset={4} className="p-2 max-w-xs"
        onMouseEnter={handleContentEnter} onMouseLeave={handleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {loading ? (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--page-muted)' }} />
          </div>
        ) : items && items.length > 0 ? (
          <div className="space-y-0.5 max-h-48 overflow-y-auto">
            {items.map((item, i) => (
              <div
                key={i}
                className="px-2 py-1.5 rounded text-sm cursor-pointer transition-colors hover:bg-[#377AD6]/8"
                onClick={(e) => { e.stopPropagation(); if (item.url) navigate(item.url); }}
              >
                <div className="font-medium" style={{ color: 'var(--page-text)' }}>{item.label}</div>
                {item.sublabel && <div className="text-xs" style={{ color: 'var(--page-muted)' }}>{item.sublabel}</div>}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-2 py-1.5 text-sm" style={{ color: 'var(--page-muted)' }}>None</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const COL = {
  name:     'flex-1 min-w-0',
  type:     'w-[120px] shrink-0',
  status:   'w-[130px] shrink-0',
  mappings: 'w-[90px]  shrink-0',
  rules:    'w-[70px]  shrink-0',
  ref:      'w-[80px]  shrink-0',
  actions:  'w-[50px]  shrink-0',
};

export default function ElementsTable({ elements, isLoading, onDeleteElement }) {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (element) => { setElementToDelete(element); setDeleteDialogOpen(true); };

  const handleConfirmDelete = async () => {
    if (!elementToDelete || !onDeleteElement) return;
    setIsDeleting(true);
    try {
      await onDeleteElement(elementToDelete.id);
      setDeleteDialogOpen(false);
      setElementToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const loadMappings = async (elementId) => {
    const mappings = await SourceFieldMappingView.filter({ canonical_element_id: elementId }, 'source_name');
    return mappings.map(m => ({ label: m.source_name, sublabel: m.source_field_name, url: createPageUrl(`source-mappings/${m.id}`) }));
  };

  const loadRules = async (element) => {
    return (element.processing_rules || []).map(name => ({ label: name, url: createPageUrl('processing-rules') }));
  };

  const loadRefList = async (element) => {
    if (!element.reference_list_id) return [];
    try {
      const lists = await ReferenceList.filter({ id: element.reference_list_id });
      if (lists.length > 0) return [{ label: lists[0].list_name, url: createPageUrl('reference-lists') }];
    } catch { /* ignore */ }
    return [{ label: `List #${element.reference_list_id}`, url: createPageUrl('reference-lists') }];
  };

  return (
    <>
      <div className="flex flex-col min-w-[900px]">
        {/* Header */}
        <div
          className="flex items-center h-9 px-5 gap-[10px] border border-table-header"
          style={{ background: 'var(--table-header-bg)', borderRadius: '8px 8px 0 0' }}
        >
          <div className={`${COL.name} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Element name</div>
          <div className={`${COL.type} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Data type</div>
          <div className={`${COL.status} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Status</div>
          <div className={`${COL.mappings} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Mappings</div>
          <div className={`${COL.rules} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Rules</div>
          <div className={`${COL.ref} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Ref list</div>
          <div className={COL.actions} />
        </div>

        {/* Rows */}
        {isLoading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center h-11 px-5 gap-[10px]"
              style={{ background: i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)' }}>
              <Skeleton className="h-4 w-48" />
            </div>
          ))
        ) : elements.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm" style={{ color: 'var(--page-muted)' }}>No elements found</p>
          </div>
        ) : (
          elements.map((element, i) => {
            const mappingCount = parseInt(element.mapping_count, 10) || 0;
            const ruleCount = parseInt(element.rule_count, 10) || 0;
            const statusCfg = STATUS_CONFIG[element.status?.toUpperCase()];
            const typePill = getDataTypePill(element.data_type);
            const rowBg = i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)';

            return (
              <div
                key={element.id}
                className="group flex items-center h-11 px-5 gap-[10px] cursor-pointer"
                style={{ background: rowBg }}
                onClick={() => navigate(createPageUrl(`element-details?id=${element.id}`))}
              >
                <div className={`${COL.name} min-w-0`}>
                  <div className="text-sm font-medium truncate text-page-text group-hover:text-nav-active transition-colors">{element.canonical_name}</div>
                  {element.definition && (
                    <div className="text-xs truncate" style={{ color: 'var(--page-muted)', fontWeight: 400 }}>{element.definition}</div>
                  )}
                </div>
                <div className={COL.type}>
                  {element.data_type ? <Pill {...typePill} /> : <span style={{ color: 'var(--page-muted)' }}>—</span>}
                </div>
                <div className={COL.status}>
                  {statusCfg
                    ? <Pill bg={statusCfg.bg} color={statusCfg.color} label={statusCfg.label} />
                    : <span style={{ color: 'var(--page-muted)' }}>—</span>
                  }
                </div>
                <div className={COL.mappings}>
                  {mappingCount > 0 ? (
                    <HoverPopupCell elementId={element.id} loadContent={loadMappings} navigate={navigate}>
                      <div className="flex items-center gap-1 text-sm cursor-pointer" style={{ color: '#377AD6' }}>
                        <GitBranch size={13} />
                        {mappingCount}
                      </div>
                    </HoverPopupCell>
                  ) : (
                    <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--page-muted)' }}>
                      <GitBranch size={13} />0
                    </div>
                  )}
                </div>
                <div className={COL.rules}>
                  {ruleCount > 0 ? (
                    <HoverPopupCell elementId={element} loadContent={loadRules} navigate={navigate}>
                      <div className="flex items-center gap-1 text-sm cursor-pointer" style={{ color: '#377AD6' }}>
                        <Code size={13} />
                        {ruleCount}
                      </div>
                    </HoverPopupCell>
                  ) : (
                    <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--page-muted)' }}>
                      <Code size={13} />0
                    </div>
                  )}
                </div>
                <div className={COL.ref}>
                  {element.has_reference_list ? (
                    <HoverPopupCell elementId={element} loadContent={loadRefList} navigate={navigate}>
                      <div className="flex items-center cursor-pointer">
                        <Check size={16} color="rgba(77,179,0,0.9)" />
                      </div>
                    </HoverPopupCell>
                  ) : (
                    <span style={{ color: 'var(--page-muted)' }}>—</span>
                  )}
                </div>
                <div className={`${COL.actions} flex justify-end`} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleDeleteClick(element)}
                    className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Data Element</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold">{elementToDelete?.canonical_name}</span>?
              This will permanently remove the data element and all its versions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
